import { useState, useEffect, useCallback } from "react";
import { Plug, Wifi, WifiOff, Loader2, Play, CheckCircle2, AlertCircle, Clock, CalendarClock, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { brightApi } from "@/lib/api";

export default function BrightDataSync() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [apps, setApps] = useState([]);
  const [logs, setLogs] = useState([]);
  const [connectionOk, setConnectionOk] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const [settingsForm, setSettingsForm] = useState({
    api_key: "",
    default_date_mode: "month",
    default_month: "",
    default_date: "",
    daily_sync_enabled: false,
    daily_sync_time: "03:00",
    selected_app_uuids: [],
  });

  const [syncForm, setSyncForm] = useState({
    app_uuids: [],
    date_mode: "month",
    month: "",
    date: "",
  });

  const fetchAll = useCallback(async () => {
    try {
      const [settingsRes, appsRes, logsRes] = await Promise.all([
        brightApi.getSettings(),
        brightApi.getApps(),
        brightApi.getSyncLogs(),
      ]);
      const s = settingsRes.data;
      setSettings(s);
      setApps(appsRes.data);
      setLogs(logsRes.data);
      if (s.api_key_set) setConnectionOk(true);
      setSettingsForm((prev) => ({
        ...prev,
        default_date_mode: s.default_date_mode || "month",
        default_month: s.default_month || "",
        default_date: s.default_date || "",
        daily_sync_enabled: s.daily_sync_enabled || false,
        daily_sync_time: s.daily_sync_time || "03:00",
        selected_app_uuids: s.selected_app_uuids || [],
      }));
      setSyncForm((prev) => ({
        ...prev,
        app_uuids: s.selected_app_uuids || [],
        date_mode: s.default_date_mode || "month",
        month: s.default_month || "",
        date: s.default_date || "",
      }));
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = { ...settingsForm };
      if (!payload.api_key) delete payload.api_key;
      await brightApi.updateSettings(payload);
      toast.success("Settings saved");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!settingsForm.api_key && !settings?.api_key_set) {
      toast.error("Please enter and save an API key first");
      return;
    }
    setTesting(true);
    setConnectionOk(null);
    try {
      if (settingsForm.api_key) {
        await brightApi.updateSettings({ api_key: settingsForm.api_key });
      }
      const res = await brightApi.testConnection();
      setApps(res.data.apps);
      setConnectionOk(true);
      toast.success(`Connected! Found ${res.data.apps.length} apps`);
    } catch (err) {
      setConnectionOk(false);
      toast.error(err.response?.data?.detail || "Connection failed");
    } finally {
      setTesting(false);
    }
  };

  const toggleSettingsApp = (uuid) => {
    setSettingsForm((prev) => ({
      ...prev,
      selected_app_uuids: prev.selected_app_uuids.includes(uuid)
        ? prev.selected_app_uuids.filter((u) => u !== uuid)
        : [...prev.selected_app_uuids, uuid],
    }));
  };

  const toggleSyncApp = (uuid) => {
    setSyncForm((prev) => ({
      ...prev,
      app_uuids: prev.app_uuids.includes(uuid)
        ? prev.app_uuids.filter((u) => u !== uuid)
        : [...prev.app_uuids, uuid],
    }));
  };

  const handleSync = async () => {
    if (syncForm.app_uuids.length === 0) { toast.error("Select at least one app"); return; }
    if (syncForm.date_mode === "month" && !syncForm.month) { toast.error("Enter a month (YYYY-MM)"); return; }
    if (syncForm.date_mode === "date" && !syncForm.date) { toast.error("Enter a date (YYYY-MM-DD)"); return; }
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await brightApi.sync(syncForm);
      setSyncResult(res.data);
      toast.success(`Sync complete: ${res.data.rows_inserted} inserted, ${res.data.rows_updated} updated`);
      const logsRes = await brightApi.getSyncLogs();
      setLogs(logsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-blue-600" size={24} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="bright-data-sync-page">
      {/* Credentials */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="bright-credentials">
        <h3 className="text-sm font-semibold text-slate-900">API Credentials</h3>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">API Key {settings?.api_key_set && <span className="text-emerald-500 text-[10px]">(set)</span>}</Label>
          <Input
            type="password"
            value={settingsForm.api_key}
            onChange={(e) => setSettingsForm({ ...settingsForm, api_key: e.target.value })}
            placeholder={settings?.api_key_set ? "Leave blank to keep current" : "Enter API key"}
            className="h-9 text-sm max-w-md"
            data-testid="bright-api-key-input"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSaveSettings} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bright-save-credentials-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save Credentials
          </Button>
          <Button onClick={handleTestConnection} disabled={testing} variant="outline" size="sm" data-testid="test-connection-btn">
            {testing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plug size={14} className="mr-1.5" />} Test Connection
          </Button>
          {connectionOk === true && <span className="text-xs text-emerald-600 flex items-center gap-1"><Wifi size={14} /> Connected</span>}
          {connectionOk === false && <span className="text-xs text-red-500 flex items-center gap-1"><WifiOff size={14} /> Failed</span>}
        </div>
      </div>

      {/* Apps Selection */}
      {apps.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="bright-apps-section">
          <div className="flex items-center gap-2">
            <Smartphone size={16} className="text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">Apps</h3>
            <span className="text-xs text-slate-400 ml-1">Select apps to sync data from</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {apps.map((app) => (
              <label key={app.app_uuid} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${settingsForm.selected_app_uuids.includes(app.app_uuid) ? "border-blue-200 bg-blue-50/50" : "border-slate-100 hover:bg-slate-50"}`} data-testid={`app-${app.app_uuid}`}>
                <Checkbox checked={settingsForm.selected_app_uuids.includes(app.app_uuid)} onCheckedChange={() => toggleSettingsApp(app.app_uuid)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{app.app_name}</div>
                  <div className="text-[10px] text-slate-400 font-mono truncate">{app.appid || app.app_uuid}</div>
                </div>
                <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{app.os}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Daily Auto-Sync */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="bright-auto-sync">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Daily Auto-Sync</h3>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Switch checked={settingsForm.daily_sync_enabled} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, daily_sync_enabled: v })} data-testid="daily-sync-toggle" />
            <span className="text-sm text-slate-600">{settingsForm.daily_sync_enabled ? "Enabled" : "Disabled"}</span>
          </div>
          {settingsForm.daily_sync_enabled && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Sync Time (UTC)</Label>
              <Input value={settingsForm.daily_sync_time} onChange={(e) => setSettingsForm({ ...settingsForm, daily_sync_time: e.target.value })} className="h-8 text-sm w-[120px]" data-testid="daily-sync-time-input" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <p className="text-[11px] text-slate-400">Automatically syncs yesterday&apos;s data daily for selected apps.</p>
          <Button onClick={handleSaveSettings} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bright-save-sync-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save Settings
          </Button>
        </div>
      </div>

      {/* Manual Sync */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="bright-manual-sync">
        <h3 className="text-sm font-semibold text-slate-900">Manual Sync</h3>

        {/* App selection for sync */}
        {apps.length > 0 && (
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block">Apps to sync</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {apps.map((app) => (
                <label key={app.app_uuid} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${syncForm.app_uuids.includes(app.app_uuid) ? "border-blue-200 bg-blue-50/50" : "border-slate-100 hover:bg-slate-50"}`} data-testid={`sync-app-${app.app_uuid}`}>
                  <Checkbox checked={syncForm.app_uuids.includes(app.app_uuid)} onCheckedChange={() => toggleSyncApp(app.app_uuid)} />
                  <span className="font-medium text-slate-800 truncate text-sm">{app.app_name}</span>
                  <span className="text-[10px] text-slate-400 ml-auto">{app.os}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Date Mode</Label>
            <Select value={syncForm.date_mode} onValueChange={(v) => setSyncForm({ ...syncForm, date_mode: v })}>
              <SelectTrigger className="h-9 w-[130px] text-sm" data-testid="sync-date-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="date">Single Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">{syncForm.date_mode === "month" ? "Month" : "Date"}</Label>
            <Input
              value={syncForm.date_mode === "month" ? syncForm.month : syncForm.date}
              onChange={(e) => setSyncForm({ ...syncForm, [syncForm.date_mode === "month" ? "month" : "date"]: e.target.value })}
              placeholder={syncForm.date_mode === "month" ? "YYYY-MM" : "YYYY-MM-DD"}
              className="h-9 text-sm w-[160px]"
              data-testid="sync-date-input"
            />
          </div>
          <div className="pt-5">
            <Button onClick={handleSync} disabled={syncing} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sync-now-btn">
              {syncing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Play size={14} className="mr-1.5" />}
              {syncing ? "Syncing..." : "Sync Now"}
            </Button>
          </div>
        </div>

        {/* Sync Result */}
        {syncResult && (
          <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/50" data-testid="sync-result">
            <div className="flex items-center gap-2 mb-2">
              {syncResult.status === "success" ? <CheckCircle2 size={14} className="text-emerald-500" /> : <AlertCircle size={14} className="text-red-500" />}
              <span className="text-sm font-medium text-slate-700">Sync {syncResult.status === "success" ? "Complete" : syncResult.status === "partial_failure" ? "Partial" : "Failed"}</span>
              <span className="text-xs text-slate-400 ml-auto">{syncResult.duration_seconds}s</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="bg-white rounded p-2">
                <div className="text-slate-400 text-[10px]">Inserted</div>
                <div className="font-mono font-bold text-slate-900">{syncResult.rows_inserted}</div>
              </div>
              <div className="bg-white rounded p-2">
                <div className="text-slate-400 text-[10px]">Updated</div>
                <div className="font-mono font-bold text-slate-900">{syncResult.rows_updated}</div>
              </div>
              <div className="bg-white rounded p-2">
                <div className="text-slate-400 text-[10px]">Apps</div>
                <div className="font-mono font-bold text-slate-900">{syncResult.app_uuids?.length}</div>
              </div>
            </div>
            {syncResult.errors?.length > 0 && (
              <div className="mt-2 bg-red-50 rounded p-2 text-xs text-red-600">
                {syncResult.errors.map((e, i) => <div key={i}>{e.app_uuid}: {e.error}</div>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sync History */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="bright-sync-history">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Sync History</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Time</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Type</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Period</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Rows</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400 text-sm">No sync history yet</TableCell></TableRow>
            ) : logs.map((log, i) => (
              <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                <TableCell className="py-2.5 px-4 text-xs text-slate-500">{new Date(log.started_at).toLocaleString()}</TableCell>
                <TableCell className="py-2.5 px-4">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${log.sync_type === "manual" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600"}`}>
                    {log.sync_type}
                  </span>
                </TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-slate-600">{log.requested_month || log.requested_date || "—"}</TableCell>
                <TableCell className="py-2.5 px-4">
                  {log.status === "success" ? (
                    <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} /> Success</span>
                  ) : log.status === "failed" ? (
                    <span className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} /> Error</span>
                  ) : (
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> {log.status}</span>
                  )}
                </TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-right text-slate-700">{log.rows_inserted + log.rows_updated}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
